import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { ImageCard } from '../components/gallery/ImageCard';
import { imagesApi } from '../services/api';
import type { ColoringPageListItem, ImageFilter, SourceType, Tag } from '../types';
import { tagsApi } from '../services/api';

export function GalleryPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState<ColoringPageListItem[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ImageFilter>({
    page: 1,
    pageSize: 20,
    sortBy: 'CreatedDate',
    sortDescending: true,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadImages();
  }, [filter]);

  useEffect(() => {
    loadTags();
  }, []);

  const loadImages = async () => {
    setLoading(true);
    try {
      const response = await imagesApi.getAll(filter);
      setImages(response.items);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Failed to load images:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await tagsApi.getAll();
      setTags(response);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleEdit = (id: number) => {
    navigate(`/editor/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      await imagesApi.delete(id);
      loadImages();
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const handleDownload = (id: number) => {
    window.open(imagesApi.getFileUrl(id), '_blank');
  };

  const sourceTypes: { value: SourceType | ''; label: string }[] = [
    { value: '', label: 'All Types' },
    { value: 'Upload', label: 'Uploads' },
    { value: 'PhotoConversion', label: 'Line Art' },
    { value: 'Cleanup', label: 'Cleaned' },
    { value: 'WatermarkRemoval', label: 'Watermark Removed' },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Your Gallery</h1>
          <p className="text-gray-600 mt-1">{totalCount} coloring pages</p>
        </div>
        <button
          onClick={() => navigate('/create')}
          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all flex items-center gap-2"
        >
          <FiPlus className="w-5 h-5" />
          Upload Photo
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or filename..."
              value={filter.search || ''}
              onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 1 })}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border font-medium flex items-center gap-2 transition-colors ${
              showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FiFilter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filter.sourceType || ''}
                onChange={(e) => setFilter({ ...filter, sourceType: e.target.value as SourceType || undefined, page: 1 })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {sourceTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={filter.sortBy}
                onChange={(e) => setFilter({ ...filter, sortBy: e.target.value as ImageFilter['sortBy'], page: 1 })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="CreatedDate">Date Created</option>
                <option value="Title">Title</option>
                <option value="FileName">Filename</option>
                <option value="FileSize">File Size</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <select
                value={filter.sortDescending ? 'desc' : 'asc'}
                onChange={(e) => setFilter({ ...filter, sortDescending: e.target.value === 'desc', page: 1 })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
            {tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 5).map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => {
                        const currentTags = filter.tagIds || [];
                        const newTags = currentTags.includes(tag.id)
                          ? currentTags.filter((id) => id !== tag.id)
                          : [...currentTags, tag.id];
                        setFilter({ ...filter, tagIds: newTags.length > 0 ? newTags : undefined, page: 1 });
                      }}
                      className={`text-xs px-2 py-1 rounded-full transition-colors ${
                        filter.tagIds?.includes(tag.id)
                          ? 'bg-indigo-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : images.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-12 text-center">
          <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No coloring pages yet</h2>
          <p className="text-gray-600 mb-6">Upload a photo to create your first coloring page.</p>
          <button
            onClick={() => navigate('/create')}
            className="text-indigo-600 font-medium hover:text-indigo-700"
          >
            Upload your first photo →
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {images.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDownload={handleDownload}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => setFilter({ ...filter, page: (filter.page || 1) - 1 })}
                disabled={filter.page === 1}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <FiChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-gray-600">
                Page {filter.page} of {totalPages}
              </span>
              <button
                onClick={() => setFilter({ ...filter, page: (filter.page || 1) + 1 })}
                disabled={filter.page === totalPages}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
